import { Inbox, Link as LinkIcon, Trash2Icon } from 'lucide-react';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { OptionItem } from '@/components/inspector/entity-fields/form/option-item';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { useDeleteLinkMutation } from '@/hooks/links/useDeleteLinkMutation';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { TruncatedText } from '@/components/ui/truncated-text';
import { useNodeLinks } from '@/hooks/links/useNodeLinks';
import { useClipboard } from '@/hooks/useClipboard';
import { Badge } from '@/components/ui/badge';
import { type Link } from '@/types/link';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const SubmissionLinkRow = ({
  link,
  nodeId,
  folderName,
  readOnly,
}: {
  link: Link;
  nodeId: string;
  folderName: string;
  readOnly: boolean;
}) => {
  const { copy } = useClipboard();
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');
  const tShares = useTranslations('shares');
  const tDialogs = useTranslations('dialogs');
  const tSubmissions = useTranslations('submissions');
  const { deleteLinkMutation } = useDeleteLinkMutation();

  const contributorCount = link.emails.length;
  const hasContributors = contributorCount > 0;

  const openEditDialog = () => {
    if (readOnly) {
      return;
    }
    openModal(ModalId.EditCreateSubmissionLink, { mode: 'edit', nodeId, folderName, link });
  };

  return (
    <OptionItem optionKey={link.id} className="border-b border-ui-base py-3">
      <div className="flex w-full items-start gap-2">
        <div className="flex shrink-0 items-start pt-0.5 text-status-warning">
          <Inbox className="icon-6" />
        </div>
        <div className={cn('flex min-w-0 grow flex-col gap-1', !readOnly && 'cursor-pointer')} onClick={openEditDialog}>
          <div data-cy="submission-link-row-contributor-count">
            <Badge
              variant={hasContributors ? 'warning' : 'dark'}
              value={hasContributors ? tShares('userCount', { count: contributorCount }) : tShares('none')}
            >
              {tSubmissions('contributorsListLabel')}
            </Badge>
          </div>
          <TruncatedText text={link.url} className="text-xs text-text-gray" dataCy="submission-link-row-url" />
          <div className="flex items-baseline gap-1 text-xs text-text-base" data-cy="submission-link-row-expiry">
            <span className="shrink-0 font-bold">{tShares('expires')}:</span>{' '}
            <TruncatedText text={link.expiresAt ?? tShares('never')} />
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <OptionAction
            onClick={() => copy(link.url, tSubmissions('copied'))}
            icon={LinkIcon}
            data-cy="submission-link-row-copy"
            tooltip={tCommon('copyLink')}
          />
          {!readOnly && (
            <OptionAction
              onClick={() =>
                openModal(ModalId.DeleteConfirmationAlertDialog, {
                  'content': tDialogs('deleteSubmissionLinkConfirmation'),
                  'onClick': () => deleteLinkMutation.mutate({ nodeId, linkId: link.id }),
                  'data-cy': 'submission-link-delete-confirm',
                })
              }
              icon={Trash2Icon}
              variant="destructive"
              data-cy="submission-link-row-delete"
              tooltip={tCommon('delete')}
            />
          )}
        </div>
      </div>
    </OptionItem>
  );
};

export const SubmissionsAccordion = ({
  nodeId,
  folderName,
  readOnly,
  enabled,
}: {
  nodeId: string;
  folderName: string;
  readOnly: boolean;
  enabled: boolean;
}) => {
  const openModal = useOpenModal();
  const tSubmissions = useTranslations('submissions');

  const { linkList, linkListIsPending } = useNodeLinks({ nodeId, offset: 0, limit: 100 }, { enabled });
  const { links } = linkList;

  return (
    <div className="flex h-full flex-col" data-cy="submissions-tab">
      <Options
        disabled={linkListIsPending}
        addButtonPosition="top"
        addButtonClassName="bg-ui-base!"
        containerClassName="bg-ui-panel"
        readOnly={readOnly}
        handleAddNewOption={() => openModal(ModalId.EditCreateSubmissionLink, { mode: 'create', nodeId, folderName })}
        emptyState={<p className="p-4 text-center text-balance text-icon-base">{tSubmissions('empty')}</p>}
        fullHeight
      >
        {links.map((link) => (
          <SubmissionLinkRow key={link.id} link={link} nodeId={nodeId} folderName={folderName} readOnly={readOnly} />
        ))}
      </Options>
    </div>
  );
};
